import { useState, useEffect, useMemo } from 'react';
import { canAddThirdParty, flag } from '../../../devices/components/advLearnIn.js';

const CloudDeviceSettingsTab = ({ allDeviceTypes, selectedDeviceIds, onSave, saving, roleWeight }) => {
    const [selected, setSelected] = useState([]);

    // Sync from props when data reloads
    useEffect(() => {
        setSelected([...(selectedDeviceIds || [])]);
    }, [selectedDeviceIds]);

    // Visible device types, sorted alphabetically. Third-party types are hidden
    // below role weight 80 (same gate as the device-type pickers — see
    // canAddThirdParty). Hiding is display-only: `selected` still carries their
    // ids, so a restricted user saving this tab preserves them exactly as they
    // were rather than silently switching them off.
    const visible = useMemo(() => {
        const allowThirdParty = canAddThirdParty(roleWeight);
        return [...(allDeviceTypes || [])]
            .filter((dt) => allowThirdParty || !flag(dt.is_third_party))
            .sort((a, b) => (a.device_type_name || '').localeCompare(b.device_type_name || ''));
    }, [allDeviceTypes, roleWeight]);

    const isDirty = useMemo(() => {
        const a = [...(selectedDeviceIds || [])].sort();
        const b = [...selected].sort();
        return JSON.stringify(a) !== JSON.stringify(b);
    }, [selected, selectedDeviceIds]);

    const toggleItem = (compositeId) => {
        setSelected((prev) =>
            prev.includes(compositeId)
                ? prev.filter((x) => x !== compositeId)
                : [...prev, compositeId]
        );
    };

    const handleSave = () => {
        onSave(selected);
    };

    const handleCancel = () => {
        setSelected([...(selectedDeviceIds || [])]);
    };

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <div>
                    <h2 className="pp-section__title">AFR Device Settings</h2>
                    <p className="ss-section-desc">
                        Select which device types should be included in the AFR (Accept, Finish, Release)
                        alert workflow. Only selected device types will generate actionable alerts.
                    </p>
                </div>
                {isDirty ? (
                    <div className="pp-section__actions">
                        <button className="dp-btn dp-btn--cancel" onClick={handleCancel}>
                            Cancel
                        </button>
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="pp-section__body">
                {visible.length === 0 ? (
                    <div className="ss-empty">
                        <p className="ss-empty__text">No device types available.</p>
                    </div>
                ) : (
                    <div className="ss-checkbox-grid">
                        {visible.map((dt) => {
                            const compositeId = `${dt.device_type_id}-${dt.manufacture_id}`;
                            return (
                                <label key={compositeId} className="ss-checkbox-item">
                                    <input
                                        className="ss-checkbox-item__input"
                                        type="checkbox"
                                        checked={selected.includes(compositeId)}
                                        onChange={() => toggleItem(compositeId)}
                                    />
                                    <span className="ss-checkbox-item__label">
                                        {dt.device_type_name}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CloudDeviceSettingsTab;
